// SystemGroup — collapsible section rendering all items for one
// System on the RoomCaptureScreen, grouped into SubSystem subsections
// (buildspec 20-ui §3 inventory, §5.4a ordering, §5.5 AssetRow).
//
// The RoomCaptureScreen hands this component one System's slice of a
// space's capture items already filtered by `systemId`. Sub-system
// grouping and per-item sort happen here so room-capture-screen.tsx
// stays focused on loading + mutation wiring. The sort follows §5.4a:
// SubSystems by `subSystemName` ASC, items by `assetTypeName` ASC —
// `CaptureItemRow` does not carry per-entity `displayOrder`, so the
// joined names are the authoritative sort keys. Stability of the JS
// `Array.prototype.sort` plus `localeCompare` gives us deterministic
// order across re-renders without allocating extra maps.
//
// Collapse affordance is a real `<button>` (not an `<h2 onClick>` —
// buttons carry role/keyboard semantics for free), toggling
// `aria-expanded` and controlling the subsections through
// `aria-controls`. The Lucide chevrons mirror `floor-section.tsx` so
// the two collapsible surfaces feel identical.

import { ChevronDown, ChevronRight } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react'

import type { CaptureItemRow } from '@citycare/shared'

import { AssetRow, type AssetRowPatch } from './asset-row'

export interface SystemGroupProps {
  systemName: string
  items: CaptureItemRow[]
  onUpdate: (itemId: string, patch: AssetRowPatch) => void
  // Fired when an AssetRow's counter is tapped past zero. Forwarded
  // straight through; the room-capture screen is responsible for the
  // mutation + undo toast.
  onRemove?: (assetTypeId: string) => void
  variant?: 'default' | 'compact'
  defaultCollapsed?: boolean
  // Optional localStorage key for persisting the collapse state across
  // reloads. When provided, the component hydrates initial state from
  // localStorage on mount and writes back on every toggle. Storage
  // failures (private browsing, quota exceeded) are swallowed — the
  // component falls back to in-memory state without breaking the UI.
  // Callers should derive a stable, scoped key like
  // `citycare:capture-collapse:${spaceId}:${systemId}` so collapse
  // state belongs to the room + system, not bleeding across spaces.
  storageKey?: string
}

function readPersistedCollapsed(storageKey: string | undefined): boolean | null {
  if (storageKey === undefined) return null
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (raw === null) return null
    return raw === 'true'
  } catch {
    return null
  }
}

function writePersistedCollapsed(
  storageKey: string | undefined,
  value: boolean,
): void {
  if (storageKey === undefined) return
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(storageKey, String(value))
  } catch {
    // Quota / disabled storage — fall back to in-memory state.
  }
}

interface SubSystemBucket {
  subSystemId: string
  subSystemName: string
  items: CaptureItemRow[]
}

function slugify(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

function groupBySubSystem(items: CaptureItemRow[]): SubSystemBucket[] {
  const buckets = new Map<string, SubSystemBucket>()
  for (const item of items) {
    const key = item.subSystemId
    const bucket = buckets.get(key)
    if (bucket === undefined) {
      buckets.set(key, {
        subSystemId: item.subSystemId,
        subSystemName: item.subSystemName,
        items: [item],
      })
    } else {
      bucket.items.push(item)
    }
  }
  const ordered = Array.from(buckets.values()).sort((a, b) =>
    a.subSystemName.localeCompare(b.subSystemName),
  )
  for (const bucket of ordered) {
    bucket.items.sort((a, b) => a.assetTypeName.localeCompare(b.assetTypeName))
  }
  return ordered
}

export function SystemGroup({
  systemName,
  items,
  onUpdate,
  onRemove,
  variant = 'default',
  defaultCollapsed = false,
  storageKey,
}: SystemGroupProps): ReactElement {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    const persisted = readPersistedCollapsed(storageKey)
    return persisted ?? defaultCollapsed
  })
  // RoomCaptureScreen stays mounted across `/capture/spaces/:spaceId`
  // navigation (the route component is not re-mounted on param change),
  // and `<SystemGroup>` is keyed by `systemId` only — so a "Electrical"
  // group present in both room A and room B reuses the same React
  // instance. Without this re-hydrate, the lazy `useState` initializer
  // runs once on the first mount and the new room would inherit the
  // previous room's in-memory collapse state, ignoring its own
  // persisted value until the user toggled. Re-reading whenever
  // `storageKey` changes restores the per-room contract. The ref guard
  // skips the redundant first-mount read (the lazy init already did
  // that) so we don't bounce state on initial render.
  const previousStorageKeyRef = useRef<string | undefined>(storageKey)
  useEffect(() => {
    if (previousStorageKeyRef.current === storageKey) return
    previousStorageKeyRef.current = storageKey
    const persisted = readPersistedCollapsed(storageKey)
    setIsCollapsed(persisted ?? defaultCollapsed)
  }, [storageKey, defaultCollapsed])
  const slug = slugify(systemName)
  const bodyId = `system-group-${slug}-body`
  const subSystems = useMemo(() => groupBySubSystem(items), [items])

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev
      writePersistedCollapsed(storageKey, next)
      return next
    })
  }, [storageKey])

  return (
    <section
      className="overflow-hidden rounded-2xl border border-[color:var(--color-cc-forest)]/10 bg-white shadow-sm"
      data-testid="system-group"
      data-system-name={systemName}
    >
      <button
        type="button"
        onClick={toggleCollapsed}
        aria-expanded={!isCollapsed}
        aria-controls={bodyId}
        data-testid="system-group-header"
        className="flex min-h-11 w-full items-center justify-between gap-3 bg-[color:var(--color-cc-forest)] px-4 py-3 text-left text-[color:var(--color-cc-cream)] transition hover:bg-[color:var(--color-cc-teal)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)]"
      >
        <span className="flex items-center gap-2">
          {isCollapsed ? (
            <ChevronRight aria-hidden="true" className="h-5 w-5 text-[color:var(--color-cc-lime)]" />
          ) : (
            <ChevronDown aria-hidden="true" className="h-5 w-5 text-[color:var(--color-cc-lime)]" />
          )}
          <span className="font-display text-lg font-semibold text-[color:var(--color-cc-cream)]">
            {systemName}
          </span>
        </span>
        {/* Lime count chip — `done/total` per the redesigned hero
             card spec, where "done" counts items with a positive
             quantity OR a confirmed-zero (the same completion
             signals used by the export). For an empty system the
             chip falls back to the total count so a small group
             still surfaces its size. */}
        <span className="rounded-full bg-[color:var(--color-cc-lime)] px-2.5 py-0.5 text-xs font-bold text-[color:var(--color-cc-forest)]">
          {items.length === 0
            ? '0'
            : `${items.filter((i) => parseFloat(i.quantity) > 0 || i.isZeroConfirmed).length}/${items.length}`}
        </span>
      </button>

      {isCollapsed ? null : (
        <div id={bodyId} className="space-y-4 bg-[color:var(--color-cc-cream)]/40 p-3">
          {subSystems.map((sub) => (
            <section
              key={sub.subSystemId}
              data-testid="sub-system-section"
              data-sub-system-name={sub.subSystemName}
              className="overflow-hidden rounded-xl border border-[color:var(--color-cc-forest)]/10 bg-white"
            >
              <h3 className="border-b border-[color:var(--color-cc-forest)]/10 bg-[color:var(--color-cc-cream)] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-cc-forest)]/80">
                {sub.subSystemName}
              </h3>
              <div className="space-y-2 p-2">
                {sub.items.map((item) => (
                  // Use assetTypeId as the React key + onUpdate
                  // identity. Synthetic template-only rows arrive
                  // from the server with `id: ''` (see
                  // merge-template.ts syntheticTemplateOnlyRow), so
                  // keying by `id` collapses every untaken slot in a
                  // room into the same React node and a + on one
                  // re-renders/updates all of them. assetTypeId is
                  // unique per (space, asset_type) and stable across
                  // the synthetic→persisted transition.
                  <AssetRow
                    key={item.assetTypeId}
                    item={item}
                    onUpdate={(patch) => onUpdate(item.assetTypeId, patch)}
                    onRemove={
                      onRemove === undefined
                        ? undefined
                        : () => onRemove(item.assetTypeId)
                    }
                    variant={variant}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </section>
  )
}
